import React, { useRef } from "react";
import { Download } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import {
 NandaSaMark,
 JohnTadoMark,
 CosmetiquesMark,
 NandaSaFoodMark,
 EnergiaMark,
 AcademieMark,
 KasaMark,
} from "@/components/BrandMarks";

const MARKS = [
 { key: "nanda-sa", name: "NANDA SÁ", role: { fr: "Logo principal du Groupe", en: "Main Group logo", pt: "Logo principal do Grupo" }, note: { fr: "Monogramme N sous macron, identité mère", en: "N monogram under a macron, parent identity", pt: "Monograma N sob macron, identidade mãe" }, Comp: NandaSaMark },
 { key: "john-tado", name: "JOHN TĀDO", role: { fr: "Monogramme JT. Art de vivre", en: "JT monogram. Lifestyle", pt: "Monograma JT. Arte de viver" }, note: { fr: "T horizontal avec sérifs, J descendant courbe", en: "Horizontal T with serifs, J descending curve", pt: "T horizontal com serifas, J descendente com curva" }, Comp: JohnTadoMark },
 { key: "cosmetiques", name: "NANDA SÁ Cosmétiques", role: { fr: "Symbole botanique. Cosmétique", en: "Botanical symbol. Cosmetics", pt: "Símbolo botânico. Cosméticos" }, note: { fr: "Feuille nervurée, division cosmétiques naturels", en: "Veined leaf, natural cosmetics division", pt: "Folha com nervuras, divisão de cosméticos naturais" }, Comp: CosmetiquesMark },
 { key: "nanda-sa-food", name: "NANDA SÁ · Gastronomie", role: { fr: "Épi stylisé. Gastronomie & Terroir", en: "Stylised ear. Gastronomy & Terroir", pt: "Espiga estilizada. Gastronomia & Terroir" }, note: { fr: "Grains flanquant une tige : blé, mil, épices", en: "Grains flanking a stem: wheat, millet, spices", pt: "Grãos ladeando uma haste: trigo, milho, especiarias" }, Comp: NandaSaFoodMark },
 { key: "energia", name: "ENERGIA PRO", role: { fr: "Soleil géométrique. Énergie", en: "Geometric sun. Energy", pt: "Sol geométrico. Energia" }, note: { fr: "Disque solaire et 8 rayons cardinaux", en: "Solar disc and 8 cardinal rays", pt: "Disco solar e 8 raios cardeais" }, Comp: EnergiaMark },
 { key: "academie", name: "Académie", role: { fr: "Livre ouvert. Savoir & Transmission", en: "Open book. Knowledge & Transmission", pt: "Livro aberto. Saber & Transmissão" }, note: { fr: "Deux pages en V avec lignes de texte suggérées", en: "Two pages in a V with suggested text lines", pt: "Duas páginas em V com linhas de texto sugeridas" }, Comp: AcademieMark },
 { key: "kasa", name: "KĀSĀ", role: { fr: "Symbole architectural. Habitat (futur)", en: "Architectural symbol. Habitat (future)", pt: "Símbolo arquitectural. Habitat (futuro)" }, note: { fr: "Maison géométrique avec toit et fondation", en: "Geometric house with roof and foundation", pt: "Casa geométrica com telhado e fundação" }, Comp: KasaMark },
];

const IdentityCard = ({ name, role, note, Comp, keyName }) => {
 const svgRef = useRef(null);

 const download = () => {
 const svg = svgRef.current?.querySelector("svg");
 if (!svg) return;
 const clone = svg.cloneNode(true);
 // Add explicit black stroke for standalone SVG file
 clone.setAttribute("stroke", "#111111");
 clone.setAttribute("width", "512");
 clone.setAttribute("height", "512");
 const src = new XMLSerializer.serializeToString(clone);
 const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${src}`], { type: "image/svg+xml" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url;
 a.download = `nanda-sa-${keyName}-monogram.svg`;
 document.body.appendChild(a);
 a.click;
 document.body.removeChild(a);
 URL.revokeObjectURL(url);
 };

 return (
 <StaggerItem className="bg-nanda-bg p-10 md:p-12 group" data-testid={`identity-${keyName}`}>
 <div ref={svgRef} className="aspect-square bg-nanda-bgAlt flex items-center justify-center mb-8 group-hover:bg-nanda-line/30 transition-colors duration-700 border border-nanda-line/50">
 <Comp size={128} className="text-nanda-ink" />
 </div>
 <div className="font-serif text-2xl md:text-3xl text-nanda-ink leading-tight">{name}</div>
 <div className="label-overline text-nanda-terracotta mt-3">{role}</div>
 <p className="mt-4 text-nanda-inkSoft text-sm leading-relaxed">{note}</p>
 <button
 type="button"
 onClick={download}
 className="mt-6 inline-flex items-center gap-2 label-overline text-nanda-ink hover:text-nanda-terracotta transition-colors border-b border-nanda-line pb-1"
 data-testid={`identity-download-${keyName}`}
 >
 <Download size={14} strokeWidth={1.5} /> SVG
 </button>
 </StaggerItem>
 );
};

export default function IdentityKit() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;

 return (
 <div data-testid="page-identity">
 {/* Hero */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-4">
 <div className="label-overline text-nanda-muted">{l("Identité visuelle", "Visual identity", "Identidade visual")}</div>
 <a
 href="/nanda-sa-style-guide.pdf"
 target="_blank"
 rel="noopener noreferrer"
 data-testid="download-style-guide-pdf"
 className="mt-6 inline-flex items-center gap-3 bg-nanda-terracotta text-white px-6 py-3 hover:bg-[#8F5B3F] transition-colors duration-500 label-overline"
 >
 <Download size={14} strokeWidth={1.5} />
 {l("Style Guide PDF", "Style Guide PDF", "Style Guide PDF")}
 </a>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-balance leading-[1.0]">
 {l("Un système. Sept écritures.", "One system. Seven writings.", "Um sistema. Sete escritas.")}
 </h1>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">
 {l(
 "Le système d'identité NANDA SÁ compose un logo mère et un monogramme propre à chaque Maison. Une même main graphique : monoline, géométrique, africaine contemporaine. Téléchargez librement chaque symbole en SVG pour vos catalogues, présentations et réseaux sociaux.",
 "The NANDA SÁ identity system pairs a parent logo with a monogram for each House. One graphic hand: monoline, geometric, contemporary African. Download each symbol in SVG for your catalogues, decks and social media.",
 "O sistema de identidade NANDA SÁ combina um logotipo mãe e um monograma para cada Casa. Uma mesma mão gráfica: monolinha, geométrica, africana contemporânea. Descarregue cada símbolo em SVG para os seus catálogos, apresentações e redes sociais."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* Grid of monograms */}
 <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {MARKS.map((m) => (
 <IdentityCard
 key={m.key}
 keyName={m.key}
 name={m.name}
 role={l(m.role.fr, m.role.en, m.role.pt)}
 note={l(m.note.fr, m.note.en, m.note.pt)}
 Comp={m.Comp}
 />
 ))}
 </Stagger>
 </section>

 {/* Usage guidelines */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-6">{l("Règles d'usage", "Usage rules", "Regras de utilização")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-12">
 {l("Une écriture partagée.", "A shared writing.", "Uma escrita partilhada.")}
 </h2>
 <ul className="grid grid-cols-1 md:grid-cols-2 gap-8 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 <li><span className="text-nanda-ink font-medium">{l("Monoline stricte", "Strict monoline", "Monolinha estrita")}</span>, {l("épaisseur constante de 1,5 pt sur toutes les Maisons.", "constant 1.5 pt weight across every House.", "espessura constante de 1,5 pt em todas as Casas.")}</li>
 <li><span className="text-nanda-ink font-medium">{l("Cercle unificateur", "Unifying circle", "Círculo unificador")}</span>, {l("chaque monogramme s'inscrit dans un cercle de 26 unités.", "every monogram sits inside a 26-unit circle.", "cada monograma inscreve-se num círculo de 26 unidades.")}</li>
 <li><span className="text-nanda-ink font-medium">{l("Couleur d'encre", "Ink colour", "Cor de tinta")}</span>, {l("noir NANDA SÁ #111 ou terracotta #A66E4E sur fond ivoire.", "NANDA SÁ black #111 or terracotta #A66E4E on ivory.", "preto NANDA SÁ #111 ou terracota #A66E4E em marfim.")}</li>
 <li><span className="text-nanda-ink font-medium">{l("Espace de protection", "Clear space", "Espaço de protecção")}</span>, {l("marge minimum = ½ de la hauteur du symbole.", "minimum margin = ½ of the symbol height.", "margem mínima = ½ da altura do símbolo.")}</li>
 <li><span className="text-nanda-ink font-medium">{l("Taille minimum", "Minimum size", "Tamanho mínimo")}</span>, {l("16 px pour l'écran, 8 mm pour l'impression.", "16 px on screen, 8 mm in print.", "16 px em ecrã, 8 mm em impressão.")}</li>
 <li><span className="text-nanda-ink font-medium">{l("Fond autorisé", "Allowed backgrounds", "Fundos permitidos")}</span>, {l("uni, sans dégradé, sans photographie sous le symbole.", "solid, no gradient, no photograph under the symbol.", "sólido, sem gradiente, sem fotografia sob o símbolo.")}</li>
 </ul>
 </FadeUp>
 </div>
 </section>
 </div>
 );
}
